import { Suspense, type ReactNode, type ErrorInfo, useState } from "react";
import { ErrorBoundary } from "react-error-boundary";
import { cn } from "@/lib/utils";
import { SidebarProvider } from "@/components/ui/sidebar";
import { Notification } from "@/components/admin/notification";
import { Error } from "@/components/admin/error";
import { Skeleton } from "@/components/ui/skeleton";

import { useConfigurationLoader } from "../root/useConfigurationLoader";
import { CrmSidebar } from "./CrmSidebar";
import { TopBar } from "./TopBar";
import { ProcessingJobsProvider } from "@/contexts/ProcessingJobsContext";
// P091 T0106/E10 — the ONE monetization popup. Mounted here, once, so every surface in the CRM
// (the send gate's 402, the credits flag, a settings button) opens the same dialog with the same
// stone-ladder prices instead of each rendering its own paywall. Open it from anywhere with
// `useMonetization().open(reason)`.
import { MonetizationProvider } from "@/components/monetization/MonetizationProvider";
// P091 — the wallet, CENTRED under the top edge. glowtest_FE's shape (`#subscriptions-center`:
// fixed, left/right 0, `w-fit`, `mx-auto`, rounded bottom corners) — first shipped as a control in
// the header's right cluster and rejected on sight: a balance is not one more tool among
// ThemeToggle/Refresh/UserMenu.
import { CreditsCenter } from "@/components/monetization/CreditsCenter";

export const Layout = ({ children }: { children: ReactNode }) => {
  useConfigurationLoader();
  const [errorInfo, setErrorInfo] = useState<ErrorInfo | undefined>(undefined);
  const handleError = (_: unknown, info: ErrorInfo) => {
    setErrorInfo(info);
  };

  return (
    <ProcessingJobsProvider>
      <MonetizationProvider>
      <SidebarProvider className="bg-background">
        <CrmSidebar />
        <main
          className={cn(
            "bg-background text-foreground",
            "ml-auto w-full max-w-full",
            "peer-data-[state=collapsed]:w-[calc(100%-var(--sidebar-width-icon)-1rem)]",
            "peer-data-[state=expanded]:w-[calc(100%-var(--sidebar-width))]",
            "sm:transition-[width] sm:duration-200 sm:ease-linear",
            "flex h-svh flex-col",
          )}
        >
          <TopBar />
          <CreditsCenter />
          <ErrorBoundary
            onError={handleError}
            fallbackRender={({ error, resetErrorBoundary }) => (
              <Error
                error={error}
                errorInfo={errorInfo}
                resetErrorBoundary={resetErrorBoundary}
              />
            )}
          >
            <Suspense fallback={<Skeleton className="h-12 w-12 rounded-full" />}>
              <div className="flex flex-1 flex-col px-4 pt-4 overflow-auto">
                {children}
              </div>
            </Suspense>
          </ErrorBoundary>
        </main>
        <Notification />
      </SidebarProvider>
      </MonetizationProvider>
    </ProcessingJobsProvider>
  );
};
